import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import {
  LayoutDashboard,
  Sparkles,
  Lightbulb,
  Package,
  FolderOpen,
  Calendar,
  BookOpen,
  LogOut,
  Menu,
  X,
  User,
  ListTodo,
  Building2,
  ChevronDown,
  ChevronRight,
  Users,
  Target,
  StickyNote,
  Gem,
  UserPlus,
  Video,
  Film,
  Wand2,
  Palette,
  ShoppingBag,
  Contact,
  Globe,
  Star,
  Zap,
  Megaphone,
  Search,
  MessageCircle,
  Type,
  FileImage,
  Boxes,
  Brain,
  Tag,
  Store,
  LayoutGrid,
  Box,
  Mic,
  Award,
  Clapperboard,
  Instagram,
  Terminal,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const FOUNDER_EMAIL = "ansh@thelocaljewel.com";

// Users who can see the Platforms tab
const PLATFORMS_ALLOWED_EMAILS = ["ansh@thelocaljewel.com", "nayan@thelocaljewel.com"];

// Users who can see the Media Generator tab (founder + allowlist)
const MEDIA_GEN_ALLOWED_EMAILS = ["ansh@thelocaljewel.com", "nayan@thelocaljewel.com"];

// Ideas submenu items
const ideasSubItems = [
  { path: '/ideas/content', label: 'Content', icon: Video },
  { path: '/ideas/custom-ip', label: 'Custom IP', icon: Palette },
  { path: '/ideas/celebrity-ip', label: 'Celebrity IP', icon: Star },
  { path: '/ideas/reddit', label: 'Reddit', icon: MessageCircle },
];

// Editor-only nav items (video editors only see Production)
const editorNavItems = [
  { path: '/production-packs', label: 'Production', icon: Package },
];

const navItems = [
  { path: '/', label: 'Home', icon: LayoutDashboard },
  { path: '/media-generator', label: 'Media Generator', icon: Sparkles, mediaGenOnly: true },
  { path: '/story-generator', label: 'Story Generator', icon: Wand2 },
  { path: '/caption-generator', label: 'Caption Generator', icon: Type },
  { path: '/description-generator', label: 'Description Generator', icon: FileImage },
  { path: '/title-generator', label: 'Title Generator', icon: Tag },
  { path: '/mood-board', label: 'Mood Board Generator', icon: LayoutGrid },
  { path: '/render-3d', label: '3D Render Generator', icon: Box },
  { path: '/olivia', label: 'Olivia', icon: Mic },
  { path: '/etsy', label: 'Listings', icon: Store },
  { path: '/ad-creatives', label: 'Ad Creatives', icon: Clapperboard },
  { path: '/story-sets', label: 'Story Sets', icon: Instagram },
  { path: '/video-bank', label: 'Video Bank', icon: Film },
  { path: '/incentives', label: 'Incentives', icon: Award, localJewelOnly: true },
  { path: '/ideas', label: 'Ideas', icon: Lightbulb, hasSubmenu: true, subItems: ideasSubItems },
  { path: '/production-packs', label: 'Production', icon: Package },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/brand-library', label: 'Brand', icon: BookOpen },
  { path: '/todo', label: 'To-Do', icon: ListTodo },
  { path: '/notes', label: 'Notes', icon: StickyNote },
  { path: '/creator-outreach', label: 'Creator Outreach', icon: UserPlus },
];

// Founder-only nav items
const founderNavItems = [
  { path: '/developers', label: 'Agent API', icon: Terminal },
  { path: '/contacts', label: 'Contacts', icon: Contact },
  { path: '/team', label: 'Team', icon: Users },
];

const REDDIT_LEADS_ALLOWED = ['ansh@thelocaljewel.com', 'nayan@thelocaljewel.com', 'rhea@thelocaljewel.com'];

const MainLayout = () => {
  const { user, logout, isFounder, activeBrand, switchBrand, getActiveBrandName, brands, showMoneyGoalPopup, setShowMoneyGoalPopup, updateMoneyGoal } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moneyGoalInput, setMoneyGoalInput] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({ ideas: true }); // Ideas expanded by default
  
  const isFounderUser = isFounder();
  const isEditor = user?.role === 'editor';
  const canSeePlatforms = PLATFORMS_ALLOWED_EMAILS.includes(user?.email);
  const canSeeMediaGen = MEDIA_GEN_ALLOWED_EMAILS.includes(user?.email);
  const canSeeRedditLeads = REDDIT_LEADS_ALLOWED.includes(user?.email);
  
  const isLocalJewelUser = user?.email?.endsWith('@thelocaljewel.com');

  // Build nav items based on access
  const allNavItems = isEditor ? editorNavItems : [
    ...navItems.filter((item) => {
      if (item.founderOnly && !isFounderUser) return false;
      if (item.mediaGenOnly && !canSeeMediaGen) return false;
      if (item.localJewelOnly && !isLocalJewelUser) return false;
      return true;
    }),
    { path: '/ads', label: 'Ads', icon: Megaphone },
    ...(isLocalJewelUser ? [{ path: '/research', label: 'Research', icon: Search }] : []),
    ...(canSeePlatforms ? [{ path: '/platforms', label: 'Platforms', icon: Globe }] : []),
    ...(isFounderUser ? founderNavItems : []),
    ...(canSeeRedditLeads ? [{ path: '/reddit-leads', label: 'Reddit AI Lead Gen', icon: Zap }] : []),
  ];

  const toggleSubmenu = (key) => {
    setExpandedMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveMoneyGoal = async () => {
    if (!moneyGoalInput.trim()) {
      toast.error('Please enter your goal');
      return;
    }
    
    try {
      setSavingGoal(true);
      await updateMoneyGoal(moneyGoalInput.trim());
      toast.success('Goal saved! Let\'s crush it! 💪');
    } catch (error) {
      toast.error('Failed to save goal');
    } finally {
      setSavingGoal(false);
    }
  };

  // Brand Switcher Component (for founder only)
  const BrandSwitcher = () => {
    if (!isFounderUser) return null;
    
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-between text-sm font-medium bg-zinc-50 border-zinc-200 hover:bg-zinc-100"
            data-testid="brand-switcher"
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-zinc-500" />
              <span className="truncate">{getActiveBrandName()}</span>
            </div>
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {brands.map((brand) => (
            <DropdownMenuItem
              key={brand.id}
              onClick={() => switchBrand(brand.id)}
              className={cn(
                "cursor-pointer",
                activeBrand === brand.id && "bg-zinc-100 font-medium"
              )}
              data-testid={`brand-option-${brand.id}`}
            >
              <Building2 className="w-4 h-4 mr-2" />
              {brand.name}
              {activeBrand === brand.id && (
                <span className="ml-auto text-xs text-zinc-500">Active</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="main-layout">
      {/* Desktop Sidebar */}
      <aside 
        className="hidden md:flex w-64 border-r border-[#E8E1D5] bg-[#F4EFE6]/80 backdrop-blur-xl h-screen fixed top-0 left-0 flex-col p-6 z-40"
        data-testid="desktop-sidebar"
      >
        {/* Logo / Money Goal */}
        <div className="mb-6">
          {user?.money_goal ? (
            <>
              <p className="text-xs uppercase tracking-widest text-zinc-400">Road to</p>
              <h1 className="text-xl font-semibold tracking-tight text-zinc-900 mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                {user.money_goal} 💪💵
              </h1>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Content OS
              </h1>
              <p className="text-xs uppercase tracking-widest text-zinc-400 mt-1">Jewelry Brand</p>
            </>
          )}
        </div>

        {/* Brand Switcher (for founder only) */}
        {isFounderUser && (
          <div className="mb-6">
            <BrandSwitcher />
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto min-h-0">
          {allNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.hasSubmenu 
              ? location.pathname.startsWith(item.path)
              : location.pathname === item.path;
            
            // Handle items with submenus
            if (item.hasSubmenu) {
              const isExpanded = expandedMenus[item.label.toLowerCase()];
              return (
                <div key={item.path}>
                  <button
                    onClick={() => toggleSubmenu(item.label.toLowerCase())}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={cn(
                      "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-colors duration-200",
                      isActive 
                        ? "bg-white text-[#2D2623]" 
                        : "text-[#6E635B] hover:text-[#2D2623] hover:bg-white/80"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-transform duration-200",
                      isExpanded && "rotate-90"
                    )} />
                  </button>
                  
                  {/* Submenu */}
                  {isExpanded && (
                    <div className="ml-4 mt-1 space-y-1 border-l border-zinc-200 pl-4">
                      {item.subItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = location.pathname === subItem.path;
                        return (
                          <NavLink
                            key={subItem.path}
                            to={subItem.path}
                            data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={cn(
                              "flex items-center gap-3 px-4 py-2 rounded-xl transition-colors duration-200 text-sm",
                              isSubActive 
                                ? "bg-[#2D2623] text-white shadow-sm" 
                                : "text-[#6E635B] hover:text-[#2D2623] hover:bg-white/80"
                            )}
                          >
                            <SubIcon className="w-4 h-4" strokeWidth={1.5} />
                            <span className="font-medium">{subItem.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200",
                  isActive 
                    ? "bg-[#2D2623] text-white shadow-sm" 
                    : "text-[#6E635B] hover:text-[#2D2623] hover:bg-white/80"
                )}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="pt-6 border-t border-[#E8E1D5] mt-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white border border-[#E8E1D5] flex items-center justify-center">
              <User className="w-4 h-4 text-zinc-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
              <p className="text-xs text-zinc-400 capitalize">{user?.role}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-zinc-400 hover:text-zinc-900"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-[#FAF7F2]/90 backdrop-blur-xl border-b border-[#E8E1D5] flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-3">
          {user?.money_goal ? (
            <h1 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Road to {user.money_goal} 💪💵
            </h1>
          ) : (
            <h1 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Content OS
            </h1>
          )}
          {isFounderUser && (
            <span className="text-xs bg-zinc-100 px-2 py-1 rounded text-zinc-600">
              {getActiveBrandName()}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-[#FAF7F2] z-40 pt-16 flex flex-col" data-testid="mobile-menu">
          {/* Mobile Brand Switcher */}
          {isFounderUser && (
            <div className="p-4 border-b border-zinc-100 shrink-0">
              <BrandSwitcher />
            </div>
          )}
          <nav className="p-4 space-y-1 flex-1 overflow-y-auto min-h-0 pb-24" data-testid="mobile-menu-nav">
            {allNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.hasSubmenu 
                ? location.pathname.startsWith(item.path)
                : location.pathname === item.path;
              
              // Handle items with submenus
              if (item.hasSubmenu) {
                const isExpanded = expandedMenus[item.label.toLowerCase()];
                return (
                  <div key={item.path}>
                    <button
                      onClick={() => toggleSubmenu(item.label.toLowerCase())}
                      className={cn(
                        "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-colors",
                        isActive 
                          ? "bg-white text-[#2D2623]" 
                          : "text-[#6E635B] hover:bg-[#F7F3EC]"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4" strokeWidth={1.5} />
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      <ChevronRight className={cn(
                        "w-4 h-4 transition-transform duration-200",
                        isExpanded && "rotate-90"
                      )} />
                    </button>
                    
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-1 border-l border-zinc-200 pl-4">
                        {item.subItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <NavLink
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={cn(
                                "flex items-center gap-3 px-4 py-2 rounded-xl transition-colors text-sm",
                                isSubActive 
                                  ? "bg-[#2D2623] text-white shadow-sm" 
                                  : "text-[#6E635B] hover:bg-[#F7F3EC]"
                              )}
                            >
                              <SubIcon className="w-4 h-4" strokeWidth={1.5} />
                              <span className="font-medium">{subItem.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-colors",
                    isActive 
                      ? "bg-[#2D2623] text-white shadow-sm" 
                      : "text-[#6E635B] hover:bg-[#F7F3EC]"
                  )}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm font-medium">{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
          <div className="shrink-0 p-4 border-t border-zinc-100 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center">
                <User className="w-5 h-5 text-zinc-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
                <p className="text-xs text-zinc-400 capitalize">{user?.role}</p>
              </div>
              <Button variant="ghost" onClick={logout} data-testid="mobile-logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0">
        <div className="p-6 md:p-10 lg:p-12">
          <Outlet />
        </div>
      </main>

      {/* Money Goal Popup */}
      <Dialog open={showMoneyGoalPopup && !isEditor} onOpenChange={(o) => { if (!o) sessionStorage.setItem('moneyGoalDismissed', '1'); setShowMoneyGoalPopup(o); }}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800" data-testid="money-goal-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Let's Set Your Goal! 🎯
            </DialogTitle>
            <DialogDescription className="text-center text-base mt-2 text-zinc-300">
              How much money do you want to earn in the next 3 years?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <input
              type="text"
              value={moneyGoalInput}
              onChange={(e) => setMoneyGoalInput(e.target.value)}
              className="w-full px-4 py-4 text-xl text-center border-2 border-zinc-600 rounded-md bg-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              placeholder="e.g., ₹10 Crore, $1 Million"
              data-testid="money-goal-input"
              autoFocus
            />
            
            <Button
              onClick={handleSaveMoneyGoal}
              disabled={savingGoal}
              className="w-full bg-amber-500 text-zinc-900 hover:bg-amber-400 rounded-md py-4 text-sm font-bold tracking-wide transition-all"
              data-testid="save-money-goal-btn"
            >
              {savingGoal ? 'Saving...' : 'Let\'s Go! 💪💵'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MainLayout;
